import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, DatePicker, Input, Modal, Pagination, Select, Table, Tag, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { format, fromUnixTime } from "date-fns";
import dayjs from "dayjs";
import formatMoney from "../../../utils/formatMoney";
import paymentApi from "../../../api/paymentApi";
import batchPaymentApi from "../../../api/batchPaymentApi";
import BatchPaymentSummaryBar from "./batch-payment-summary-bar";

const BATCH_STATUS_MAP = {
    processing:  { label: "Đang xử lý",   color: "blue" },
    exported:    { label: "Đã xuất file",  color: "orange" },
    reconciling: { label: "Đang tra soát", color: "purple" },
    completed:   { label: "Hoàn tất",      color: "green" },
    cancelled:   { label: "Đã huỷ",        color: "default" },
};

const STATUS_OPTIONS = Object.entries(BATCH_STATUS_MAP).map(([value, { label }]) => ({ value, label }));

const DEFAULT_FILTER = { page: 1, limit: 20, status: undefined, keyword: "", startDate: undefined, endDate: undefined };

// Số lệnh rút tối đa nạp vào modal tạo lô (cũng là trần số lệnh của 1 lô)
const MAX_BATCH_ITEMS = 1000;

const DATE_PRESETS = [
    { label: "Hôm nay",     value: [dayjs().startOf("day"), dayjs().endOf("day")] },
    { label: "Hôm qua",     value: [dayjs().subtract(1, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")] },
    { label: "7 ngày qua",  value: [dayjs().subtract(6, "day").startOf("day"), dayjs().endOf("day")] },
    { label: "30 ngày qua", value: [dayjs().subtract(29, "day").startOf("day"), dayjs().endOf("day")] },
    { label: "Tháng này",   value: [dayjs().startOf("month"), dayjs().endOf("month")] },
    { label: "Tháng trước", value: [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")] },
];

const fmtDate = (t) => (t ? format(fromUnixTime(t), "dd/MM/yyyy HH:mm") : "—");
const fmtMoney = (v) => formatMoney.format(v || 0);

function BatchPayment() {
    const [batches, setBatches]   = useState([]);
    const [total, setTotal]       = useState(0);
    const [batchStats, setBatchStats] = useState({});
    const [loading, setLoading]   = useState(false);
    const [filter, setFilter]     = useState(DEFAULT_FILTER);
    const timer = useRef(null);

    // Create modal state
    const [showCreate, setShowCreate]       = useState(false);
    const [pendingPayments, setPendingPayments] = useState([]);
    const [selectedIds, setSelectedIds]     = useState([]);
    const [batchName, setBatchName]         = useState("");
    const [creating, setCreating]           = useState(false);

    const buildQuery = useCallback((f) => {
        const parts = [`page=${f.page}`, `limit=${f.limit}`];
        if (f.status)    parts.push(`status=${f.status}`);
        if (f.keyword)   parts.push(`keyword=${encodeURIComponent(f.keyword)}`);
        if (f.startDate) parts.push(`startDate=${f.startDate}`);
        if (f.endDate)   parts.push(`endDate=${f.endDate}`);
        return parts.join("&");
    }, []);

    const fetchBatches = useCallback(async (f) => {
        setLoading(true);
        try {
            const { data } = await batchPaymentApi.getAll(buildQuery(f));
            setBatches(data.data.batches);
            setTotal(data.data.total);
            if (data.data.stats) setBatchStats(data.data.stats);
        } catch {
            message.error("Không thể tải danh sách lô");
        } finally {
            setLoading(false);
        }
    }, [buildQuery]);

    useEffect(() => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => fetchBatches(filter), 500);
        return () => clearTimeout(timer.current);
    }, [filter, fetchBatches]);

    const setField = (key, value) => setFilter((p) => ({ ...p, [key]: value, page: 1 }));

    const handleDateChange = (value) => {
        if (!value) {
            setFilter((p) => ({ ...p, startDate: undefined, endDate: undefined, page: 1 }));
        } else {
            setFilter((p) => ({
                ...p,
                startDate: value[0].startOf("day").unix(),
                endDate:   value[1].endOf("day").unix(),
                page: 1,
            }));
        }
    };

    const openCreate = async () => {
        try {
            const { data } = await paymentApi.getAll(`status=1&limit=${MAX_BATCH_ITEMS}`);
            const available = (data.data.payments || []).filter((p) => !p.batchId || p.batchStatus === "cancelled");
            setPendingPayments(available);
            setSelectedIds([]);
            setBatchName("");
            setShowCreate(true);
        } catch {
            message.error("Không tải được danh sách lệnh rút");
        }
    };

    const handleCreate = async () => {
        if (selectedIds.length === 0) return message.warning("Chọn ít nhất 1 lệnh rút");
        if (selectedIds.length > MAX_BATCH_ITEMS) {
            return message.warning(`Mỗi lô tối đa ${MAX_BATCH_ITEMS} lệnh, hiện đang chọn ${selectedIds.length}`);
        }
        setCreating(true);
        try {
            await batchPaymentApi.create({ paymentIds: selectedIds, batchName: batchName || undefined });
            message.success("Tạo lô thành công");
            setShowCreate(false);
            fetchBatches(filter);
        } catch (err) {
            message.error(err?.response?.data?.message || "Tạo lô thất bại");
        } finally {
            setCreating(false);
        }
    };

    const columns = [
        { title: "ID", dataIndex: "batchId", key: "batchId", width: 70, render: (id) => `#${id}` },
        { title: "Tên lô", dataIndex: "batchName", key: "batchName", ellipsis: true },
        {
            title: "Trạng thái", dataIndex: "status", key: "status", width: 140,
            render: (s) => {
                const m = BATCH_STATUS_MAP[s] || { label: s, color: "default" };
                return <Tag color={m.color}>{m.label}</Tag>;
            },
        },
        { title: "Số lệnh", dataIndex: "totalItems", key: "totalItems", align: "center", width: 90 },
        {
            title: "Tổng tiền", dataIndex: "totalAmount", key: "totalAmount", align: "right", width: 130,
            render: fmtMoney,
        },
        {
            title: "Người tạo", dataIndex: "creator", key: "creator", width: 130,
            render: (u) => (u ? u.name || `#${u.userId}` : "—"),
        },
        { title: "Ngày tạo", dataIndex: "createdAt", key: "createdAt", width: 140, render: fmtDate },
        {
            title: "", key: "action", width: 80, fixed: "right",
            render: (_, row) => (
                <Link to={`/payment/batch/${row.batchId}`}>
                    <Button size="small" type="link">Chi tiết</Button>
                </Link>
            ),
        },
    ];

    const pendingColumns = [
        { title: "ID", dataIndex: "paymentId", width: 70, render: (id) => `#${id}` },
        { title: "User", dataIndex: "user", render: (u) => (u ? u.name || `#${u.userId}` : "—") },
        { title: "Ngân hàng", render: (_, r) => r.Bank?.bankName || "—" },
        { title: "STK", dataIndex: "bankAccountNumber" },
        { title: "Chủ TK", dataIndex: "bankAccountName" },
        { title: "Số tiền", dataIndex: "amount", align: "right", render: fmtMoney },
    ];

    return (
        <div className="flex-1 flex flex-col p-5 overflow-auto gap-3">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold">Thanh toán theo lô</h1>
                <div className="flex gap-2">
                    <Link to="/withdraw">
                        <Button size="small">← Lệnh rút đơn lẻ</Button>
                    </Link>
                    <Button
                        type="primary"
                        size="small"
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={openCreate}
                    >
                        Tạo lô mới
                    </Button>
                </div>
            </div>

            {/* Filter bar */}
            <div className="bg-surface border border-line rounded-lg p-3 flex flex-wrap gap-2 items-center shadow-sm">
                <Input
                    placeholder="Tên lô / ID"
                    value={filter.keyword}
                    onChange={(e) => setField("keyword", e.target.value)}
                    style={{ width: 170 }}
                    allowClear
                />
                <Select
                    placeholder="Trạng thái"
                    allowClear
                    style={{ width: 150 }}
                    options={STATUS_OPTIONS}
                    onChange={(v) => setField("status", v)}
                />
                <DatePicker.RangePicker
                    value={filter.startDate && filter.endDate
                        ? [dayjs.unix(filter.startDate), dayjs.unix(filter.endDate)]
                        : undefined}
                    onChange={handleDateChange}
                    presets={DATE_PRESETS}
                    placeholder={["Từ ngày", "Đến ngày"]}
                    format="DD/MM/YYYY"
                    style={{ width: 230 }}
                    allowClear
                />
                <Button
                    icon={<FontAwesomeIcon icon={faRotateLeft} />}
                    onClick={() => setFilter(DEFAULT_FILTER)}
                    title="Xóa bộ lọc"
                />
            </div>

            <BatchPaymentSummaryBar stats={batchStats} />

            <Table
                size="small"
                dataSource={batches}
                columns={columns}
                rowKey="batchId"
                pagination={false}
                loading={loading}
                scroll={{ x: "max-content" }}
                rowClassName={() => "whitespace-nowrap"}
            />

            <div className="flex justify-between items-center flex-wrap gap-2">
                <span className="text-sm text-content-3">Tổng: {total.toLocaleString()} lô</span>
                <Pagination
                    total={total}
                    current={filter.page}
                    pageSize={filter.limit}
                    showSizeChanger
                    pageSizeOptions={[10, 20, 50]}
                    onChange={(page, limit) => setFilter((p) => ({ ...p, page, limit }))}
                />
            </div>

            {/* Create batch modal */}
            <Modal
                open={showCreate}
                title="Tạo lô thanh toán mới"
                onCancel={() => setShowCreate(false)}
                onOk={handleCreate}
                okText="Tạo lô"
                cancelText="Huỷ"
                confirmLoading={creating}
                width={900}
            >
                <div className="flex flex-col gap-3">
                    <Input
                        placeholder={`Tên lô (tuỳ chọn, e.g. Batch ${new Date().toLocaleDateString("vi-VN")})`}
                        value={batchName}
                        onChange={(e) => setBatchName(e.target.value)}
                    />
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="text-sm text-content-3 m-0">
                            Chọn lệnh rút cần thanh toán ({selectedIds.length}/{pendingPayments.length} đã chọn,
                            tối đa {MAX_BATCH_ITEMS} lệnh/lô):
                        </p>
                        <div className="flex gap-2">
                            <Button
                                size="small"
                                onClick={() => setSelectedIds(pendingPayments.map((p) => p.paymentId))}
                                disabled={pendingPayments.length === 0}
                            >
                                Chọn tất cả
                            </Button>
                            <Button
                                size="small"
                                onClick={() => setSelectedIds([])}
                                disabled={selectedIds.length === 0}
                            >
                                Bỏ chọn
                            </Button>
                        </div>
                    </div>
                    <Table
                        dataSource={pendingPayments}
                        columns={pendingColumns}
                        rowKey="paymentId"
                        size="small"
                        pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: [20, 50, 100, 200, 500, 1000] }}
                        rowSelection={{
                            preserveSelectedRowKeys: true,
                            selectedRowKeys: selectedIds,
                            onChange: (keys) => setSelectedIds(keys),
                        }}
                        scroll={{ y: 360 }}
                    />
                </div>
            </Modal>
        </div>
    );
}

export default BatchPayment;
